import JSZip from 'jszip';

export interface SmilClip {
  id: string;
  textSrc: string;
  elementId: string;
  audioSrc: string;
  clipBegin: number;
  clipEnd: number;
}

export interface EpubChapter {
  id: string;
  title: string;
  href: string;
  htmlContent: string;
  smilClips: SmilClip[];
}

export interface ParsedEpub {
  title: string;
  chapters: EpubChapter[];
  audioBlobs: Map<string, string>; // zipPath -> ObjectURL
  missingAudioPaths: string[];
}

export function parseSmilClock(clockStr: string | null | undefined): number {
  if (!clockStr) return 0;
  const trimmed = clockStr.trim();
  if (trimmed.endsWith('ms')) {
    return parseFloat(trimmed.replace('ms', '')) / 1000;
  }
  if (trimmed.endsWith('s')) {
    return parseFloat(trimmed.replace('s', ''));
  }
  if (trimmed.includes(':')) {
    const parts = trimmed.split(':').map(parseFloat);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
  }
  const val = parseFloat(trimmed);
  return isNaN(val) ? 0 : val;
}

function resolveZipPath(baseFile: string, relativePath: string): string {
  if (!relativePath) return '';
  let cleanRel = relativePath.split('#')[0];
  if (cleanRel.startsWith('/')) cleanRel = cleanRel.slice(1);
  
  const stack = baseFile ? baseFile.split('/').filter(Boolean) : [];
  if (stack.length > 0 && baseFile.includes('.')) {
    stack.pop(); // remove filename to keep base directory
  }
  
  const parts = cleanRel.split('/');
  for (const part of parts) {
    if (part === '.' || !part) continue;
    if (part === '..') {
      if (stack.length > 0) stack.pop();
    } else {
      stack.push(part);
    }
  }
  return stack.join('/');
}

function findZipFile(zip: JSZip, targetPath: string): JSZip.JSZipObject | null {
  if (!targetPath) return null;
  if (zip.files[targetPath]) return zip.files[targetPath];
  
  // Case insensitive fallback or filename match
  const targetLower = targetPath.toLowerCase();
  const targetFilename = targetPath.split('/').pop()?.toLowerCase();
  
  for (const key of Object.keys(zip.files)) {
    if (key.toLowerCase() === targetLower) return zip.files[key];
  }
  if (targetFilename) {
    for (const key of Object.keys(zip.files)) {
      if (key.toLowerCase().endsWith(`/${targetFilename}`) || key.toLowerCase() === targetFilename) {
        return zip.files[key];
      }
    }
  }
  return null;
}

export async function parseEpubFile(arrayBuffer: ArrayBuffer): Promise<ParsedEpub> {
  const zip = await JSZip.loadAsync(arrayBuffer);
  
  // 1. Locate container.xml
  const containerFile = findZipFile(zip, 'META-INF/container.xml');
  let opfPath = 'OEBPS/content.opf';
  
  if (containerFile) {
    const containerTxt = await containerFile.async('string');
    const parser = new DOMParser();
    const doc = parser.parseFromString(containerTxt, 'application/xml');
    const rootfile = doc.querySelector('rootfile');
    if (rootfile && rootfile.getAttribute('full-path')) {
      opfPath = rootfile.getAttribute('full-path')!;
    }
  }
  
  // 2. Load OPF document
  const opfFile = findZipFile(zip, opfPath);
  if (!opfFile) {
    throw new Error(`Arquivo OPF não encontrado em: ${opfPath}`);
  }
  
  const opfTxt = await opfFile.async('string');
  const parser = new DOMParser();
  const opfDoc = parser.parseFromString(opfTxt, 'application/xml');
  
  // Title
  const titleEl = opfDoc.querySelector('title') || opfDoc.querySelector('dc\\:title');
  const title = titleEl ? titleEl.textContent || 'Livro EPUB' : 'Livro EPUB';
  
  // Manifest items
  const manifestItems = new Map<string, { href: string; mediaType: string; mediaOverlay?: string }>();
  opfDoc.querySelectorAll('manifest > item').forEach((item) => {
    const id = item.getAttribute('id');
    const href = item.getAttribute('href');
    const mediaType = item.getAttribute('media-type');
    const mediaOverlay = item.getAttribute('media-overlay');
    if (id && href) {
      manifestItems.set(id, {
        href: resolveZipPath(opfPath, href),
        mediaType: mediaType || '',
        mediaOverlay,
      });
    }
  });
  
  // Spine items
  const spineItemRefs: string[] = [];
  opfDoc.querySelectorAll('spine > itemref').forEach((itemref) => {
    const idref = itemref.getAttribute('idref');
    if (idref) spineItemRefs.push(idref);
  });
  
  // Audio files blobs
  const audioBlobs = new Map<string, string>();
  const missingAudioPaths: string[] = [];
  
  // Pre-load audio files
  for (const [_, item] of manifestItems.entries()) {
    if (item.mediaType.includes('audio') || item.href.endsWith('.mp3') || item.href.endsWith('.m4a') || item.href.endsWith('.ogg')) {
      const zFile = findZipFile(zip, item.href);
      if (zFile) {
        const blob = await zFile.async('blob');
        const objectUrl = URL.createObjectURL(blob);
        audioBlobs.set(item.href, objectUrl);
        // Also map just the filename for flexible matching
        const fileName = item.href.split('/').pop();
        if (fileName) audioBlobs.set(fileName, objectUrl);
      } else {
        missingAudioPaths.push(item.href);
      }
    }
  }
  
  // Process Chapters
  const chapters: EpubChapter[] = [];
  
  for (let i = 0; i < spineItemRefs.length; i++) {
    const idref = spineItemRefs[i];
    const item = manifestItems.get(idref);
    if (!item) continue;
    
    const xhtmlFile = findZipFile(zip, item.href);
    if (!xhtmlFile) continue;
    
    let htmlContent = await xhtmlFile.async('string');
    
    // Parse SMIL if media-overlay exists or referenced
    const smilClips: SmilClip[] = [];
    let smilHref = item.mediaOverlay ? manifestItems.get(item.mediaOverlay)?.href : null;
    
    // Fallback: check if there is a SMIL file matching chapter name
    if (!smilHref) {
      const chapterBaseName = item.href.split('/').pop()?.replace(/\.(xhtml|html|xml)$/i, '');
      for (const [_, mItem] of manifestItems.entries()) {
        if (mItem.mediaType.includes('smil') || mItem.href.endsWith('.smil')) {
          if (mItem.href.toLowerCase().includes(chapterBaseName?.toLowerCase() || '___')) {
            smilHref = mItem.href;
            break;
          }
        }
      }
    }
    
    if (smilHref) {
      const smilFile = findZipFile(zip, smilHref);
      if (smilFile) {
        const smilTxt = await smilFile.async('string');
        const smilDoc = parser.parseFromString(smilTxt, 'application/xml');
        
        smilDoc.querySelectorAll('par').forEach((par, idx) => {
          const textEl = par.querySelector('text');
          const audioEl = par.querySelector('audio');
          
          if (textEl && audioEl) {
            const rawTextSrc = textEl.getAttribute('src') || '';
            const rawAudioSrc = audioEl.getAttribute('src') || '';
            const clipBegin = parseSmilClock(audioEl.getAttribute('clipBegin'));
            const clipEnd = parseSmilClock(audioEl.getAttribute('clipEnd'));
            
            const elementId = rawTextSrc.includes('#') ? rawTextSrc.split('#')[1] : `par_${idx}`;
            const resolvedAudioPath = resolveZipPath(smilHref!, rawAudioSrc);
            
            smilClips.push({
              id: par.getAttribute('id') || `clip_${i}_${idx}`,
              textSrc: rawTextSrc,
              elementId,
              audioSrc: resolvedAudioPath,
              clipBegin,
              clipEnd,
            });
          }
        });
      }
    }
    
    // Sanitize HTML body or prepare content
    let docBody = htmlContent;
    try {
      const parsedHtml = parser.parseFromString(htmlContent, 'text/html');
      const body = parsedHtml.querySelector('body');
      if (body) {
        docBody = body.innerHTML;
      }
    } catch (e) {
      // Keep raw if DOMParser body fail
    }
    
    chapters.push({
      id: idref,
      title: `Capítulo ${i + 1}`,
      href: item.href,
      htmlContent: docBody,
      smilClips,
    });
  }
  
  return {
    title,
    chapters,
    audioBlobs,
    missingAudioPaths,
  };
}
